import React from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../../lib/reduxHooks.js';
import { useThemeStyles } from '../../lib/useThemeStyles.js';
import { selectUser, selectAuthStatus, logoutUser } from '../../store/slices/auth.js';
import { selectColorScheme, setColorScheme } from '../../store/slices/theme.js';
import NotificationBadge from '../NotificationBadge.js';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.js';
import { SidebarTrigger } from '../ui/sidebar.js';
import UserProfileDropdown from '../UserProfileDropdown.js';
import { useUserProfilesList } from '../../api/generated_hooks/generated-user_profiles-hooks.js';
import { useMyOrganization } from '../../api/my-organization-api.js';

export interface ThemedHeaderProps {
  className?: string;
  user?: { id: string; full_name?: string; email?: string }; // Accept user prop for SSR hydration
}

/**
 * Themed header component that automatically applies the correct theme colors
 */
export const ThemedHeader: React.FC<ThemedHeaderProps> = ({
  className = '',
  user: userProp,
}) => {
  // Always prefer live Redux user if present, otherwise fall back to SSR user prop
  const reduxUser = useAppSelector(selectUser);
  const user = reduxUser && reduxUser.email ? reduxUser : userProp;
  const authStatus = useAppSelector(selectAuthStatus);
  const isLoading = authStatus === 'loading';
  const colorScheme = useAppSelector(selectColorScheme);
  const dispatch = useAppDispatch();
  const { colors } = useThemeStyles();
  const navigate = useNavigate();

  // Fetch user profile to get full_name and organization_id
  const { data: userProfilesData } = useUserProfilesList({
    filters: { auth_user_id: user?.id },
  });
  const userProfile = userProfilesData?.data?.[0];

  // Organization name via the own-org endpoint (the generated
  // /organizations/:id route is admin-only). (card SkrDRgzshpV6)
  const { data: organization } = useMyOrganization();

  const handleLogout = async () => {
    await dispatch(logoutUser());
  };

  // Restore dynamic header classes
  const headerClasses = `
    py-0.5
    px-4
    ${colors.headerBackground}
    ${colors.border}
    flex-1
    min-w-0
    h-14
    flex items-center
    ${className}
  `.trim().replace(/\s+/g, ' ');

  const linkClasses = `
    ${colors.headerText}
    ${colors.headerHover}
    px-4 
    py-2 
    rounded-md 
    transition-colors 
    duration-200
  `.trim().replace(/\s+/g, ' ');

  // Bell + profile triggers follow the top bar's text color (white on colored schemes, dark on Plain).
  const triggerClasses = `${colors.headerText} ${colors.headerHover}`;

  return (
    <header className={headerClasses}>
      <div className="flex-1 min-w-0 flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <SidebarTrigger />
          <Link to="/" className={`flex items-center ${linkClasses}`}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
            <span className="font-medium">Home</span>
          </Link>
        </div>
        <div className="flex items-center space-x-2">
          <Select
            value={colorScheme}
            onValueChange={(value) => dispatch(setColorScheme(value as ColorScheme))}
          >
            <SelectTrigger className={`w-[120px] bg-transparent border-white/40 ${linkClasses}`} aria-label="Color scheme">
              <SelectValue placeholder="Color" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="blue">Blue</SelectItem>
              <SelectItem value="green">Green</SelectItem>
              <SelectItem value="purple">Purple</SelectItem>
              <SelectItem value="rose">Rose</SelectItem>
              <SelectItem value="plain">Plain</SelectItem>
            </SelectContent>
          </Select>

          {/* Notification Badge - only show when user is logged in */}
          {!isLoading && user && (
            <NotificationBadge currentUserId={user.id} triggerClassName={triggerClasses} />
          )}

          {/* Conditional rendering based on auth state */}
          {isLoading ? (
            <div className="h-8 w-24 bg-muted rounded animate-pulse"></div>
          ) : user ? (
            <UserProfileDropdown
              userName={userProfile?.full_name || user.email || 'User'}
              userEmail={user.email}
              avatarUrl={user.user_metadata?.avatar_url}
              organization={organization?.name}
              onLogout={handleLogout}
              triggerClassName={triggerClasses}
            />
          ) : (
            <UserProfileDropdown
              userName="Guest"
              userEmail={undefined}
              organization={undefined}
              onLogout={() => navigate('/login')}
              triggerClassName={triggerClasses}
            />
          )}
        </div>
      </div>
    </header>
  );
};

export default ThemedHeader;
